import api, { apiError } from "@/lib/api";
import { pricing, fmtC, fmtH } from "@/lib/calc";
import { NumCell } from "@/components/estimation/cells";
import { toast } from "sonner";

export default function PricingTab({ est, setEst, oppId }) {
  const p = pricing(est);

  const saveSettings = (patch) => {
    setEst({ ...est, ...patch });
    api.put(`/opportunities/${oppId}/estimation/settings`, patch)
      .then((r) => setEst(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  const setWsCont = (wsName, v) => {
    const workstreams = est.workstreams.map((w) => (w.name === wsName ? { ...w, contingency_pct: v || 0 } : w));
    const tasks = est.tasks.map((t) => (t.workstream === wsName ? { ...t, contingency_pct: v || 0 } : t));
    setEst({ ...est, workstreams, tasks });
    api.put(`/opportunities/${oppId}/estimation/workstreams`, { workstreams })
      .then((r) => {
        setEst(r.data);
        toast.message("Contingency updated", {
          description: `“${wsName}” WBS suggested target changed. Open the Resource Load tab and click “Sync load to suggested” to re-align the cost basis.`,
        });
      })
      .catch((e) => toast.error(apiError(e)));
  };

  const marginPctDisplay = Math.round((Number(est.margin_pct) || 0) * 100);

  return (
    <div data-testid="pricing-tab" className="grid grid-cols-1 xl:grid-cols-2 gap-8 max-w-5xl">
      <div className="space-y-8">
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <h3 className="font-heading text-sm font-medium tracking-tight mb-1">Margin</h3>
          <p className="text-xs text-zinc-400 mb-4">Applied on the resource-load cost basis</p>
          <div className="flex items-center gap-4">
            <input data-testid="margin-slider" type="range" min={0} max={80} step={1} value={marginPctDisplay}
              onChange={(e) => setEst({ ...est, margin_pct: Number(e.target.value) / 100 })}
              onMouseUp={(e) => saveSettings({ margin_pct: Number(e.target.value) / 100 })}
              onTouchEnd={(e) => saveSettings({ margin_pct: Number(e.target.value) / 100 })}
              className="flex-1 accent-[#002FA7]" />
            <div className="w-20">
              <NumCell value={est.margin_pct} scale={100} onCommit={(v) => saveSettings({ margin_pct: Math.max(0, v || 0) })}
                testId="margin-input" className="border border-zinc-300 text-base py-1.5" />
            </div>
            <span className="text-sm text-zinc-400">%</span>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <h3 className="font-heading text-sm font-medium tracking-tight mb-1">Channel partner fee</h3>
          <p className="text-xs text-zinc-400 mb-4">Applied on top of total + margin</p>
          <div className="flex items-center gap-4">
            <div className="w-20">
              <NumCell value={est.partner_fee_pct} scale={100} onCommit={(v) => saveSettings({ partner_fee_pct: Math.max(0, v || 0) })}
                testId="partner-fee-input" className="border border-zinc-300 text-base py-1.5" />
            </div>
            <span className="text-sm text-zinc-400">%</span>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-3 border-b border-zinc-200">
            <h3 className="font-heading text-sm font-medium tracking-tight">Risk contingency by workstream</h3>
            <p className="text-xs text-zinc-400 mt-0.5">Adjusts the WBS suggested baseline (target) only — the price is driven by the resource load</p>
          </div>
          {est.workstreams.length === 0 ? (
            <p className="px-5 py-6 text-xs text-zinc-400">No workstreams yet.</p>
          ) : est.workstreams.map((w) => (
            <div key={w.name} className="flex items-center justify-between gap-4 px-5 py-2.5 border-b border-zinc-100 last:border-0">
              <span className="text-[13px]">{w.name}</span>
              <div className="flex items-center gap-1.5">
                <div className="w-16"><NumCell value={w.contingency_pct} scale={100} onCommit={(v) => setWsCont(w.name, v)} testId={`ws-cont-${w.name}`} className="border border-zinc-300" /></div>
                <span className="text-xs text-zinc-400">%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm h-fit">
        <div className="px-5 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Price build-up</h3></div>
        <div className="p-5 space-y-0">
          {[
            ["Resource-load cost", fmtC(p.baseCost), `${fmtH(p.loadHours)} load hrs · avg $${p.avgCostRate.toFixed(1)}/hr`],
            [`Margin @ ${Math.round((est.margin_pct || 0) * 100)}%`, "+" + fmtC(p.marginAmount), ""],
            [`Partner fee @ ${Math.round((est.partner_fee_pct || 0) * 100)}%`, "+" + fmtC(p.partnerFee), "on cost + margin"],
          ].map(([l, v, sub]) => (
            <div key={l} className="flex items-baseline justify-between py-2.5 border-b border-zinc-100">
              <div><p className="text-[13px]">{l}</p>{sub && <p className="text-[11px] text-zinc-400">{sub}</p>}</div>
              <p className="font-mono text-sm">{v}</p>
            </div>
          ))}
          <div className="flex items-baseline justify-between pt-4">
            <p className="text-sm font-medium">Final price</p>
            <p data-testid="pricing-final-price" className="font-mono text-2xl font-medium text-[#002FA7]">{fmtC(p.finalPrice)}</p>
          </div>
          <p className="text-right text-[11px] text-zinc-400 mt-1">effective ${p.effectiveRate.toFixed(1)}/hr · gross margin {p.finalPrice > 0 ? Math.round(((p.finalPrice - p.subtotal) / p.finalPrice) * 100) : 0}%</p>

          <div data-testid="pricing-suggested-baseline" className="mt-5 pt-4 border-t border-dashed border-zinc-200 text-[12px]">
            <div className="flex items-center justify-between text-zinc-500">
              <span>WBS suggested baseline</span>
              <span className="font-mono">{fmtC(p.suggestedCost)} · {fmtH(p.suggestedHours)} hrs</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-zinc-400">Load vs baseline</span>
              <span className={`font-mono font-medium ${p.costVariance > 1 ? "text-red-600" : p.costVariance < -1 ? "text-amber-600" : "text-emerald-600"}`}>
                {p.costVariance > 0 ? "+" : ""}{fmtC(p.costVariance)}
              </span>
            </div>
            {p.loadHours === 0 && p.suggestedHours > 0 && (
              <p className="mt-2 text-[11px] text-amber-600">Resource load is empty — use “Auto-build load from estimate” on the Resource Load tab to set the cost basis.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
